import apiClient from '../apiClient';
import type { BaseResponse } from '../../types/base.dto';

// 经营数据总览请求
export interface OperatingDataRequest {
  startDate: string; // YYYY-MM-DD
  endDate: string;   // YYYY-MM-DD
  storeId?: number;
}

// 每店铺一行的经营数据
export interface OperatingDataStoreRow {
  storeId: number;
  storeName?: string;

  // 订单统计
  incomingOrderCount: number;        // 进件订单数
  approvedShippedOrderCount: number; // 过审发货订单数

  // 设备采购成本
  companySelfPurchase: number;  // 公司自采
  platformSelfPurchase: number; // 平台自采
  deviceCostTotal: number;      // 设备采购成本合计

  // 其他成本
  commission: number;     // 佣金
  accessoryCost: number;  // 配件费
  riskControlFee: number; // 风控费
  shippingFee: number;    // 发货费
  otherCostTotal: number; // 其他成本合计

  // 租金
  receivableRent: number; // 应收租金
  receivedRent: number;   // 已收租金
  pendingRent: number;    // 待收租金

  // 资产与风险
  inventoryResidualValue: number; // 库存残值（现金流手动录入）
  buyoutPrice: number;            // 租完购买价（到期购买价）
  estimatedMaxLoss: number;       // 预估最大资损

  // 预计收益 - 买断
  estimatedGrossProfitBuyout: number;    // 预估毛利（买断）
  estimatedMaxGrossMarginBuyout: number; // 预估最大毛利率（买断）%

  // 预计收益 - 归还
  estimatedGrossProfitReturn: number;    // 预估毛利（归还）
  estimatedMaxGrossMarginReturn: number; // 预估最大毛利率（归还）%
}

// 经营数据纵览响应（含KPI汇总+按店铺明细）
export interface OperatingDataOverviewResponse {
  // KPI 汇总
  incomingOrderCount: number;
  approvedShippedOrderCount: number;
  deviceCostTotal: number;
  receivableRent: number;
  receivedRent: number;
  pendingRent: number;

  // 按店铺明细
  rows: OperatingDataStoreRow[];

  updateTime: string;
}

// 手动输入数据请求
export interface ManualDataRequest {
  startDate: string;
  endDate: string;
  storeId?: number;
  dataType: 'company_self_purchase' | 'risk_control_fee';
  amount: number;
  remark?: string;
}

// 手动输入数据记录
export interface ManualData {
  id: number;
  startDate: string;
  endDate: string;
  storeId?: number;
  dataType: string;
  amount: number;
  remark?: string;
  createdAt: string;
  updatedAt: string;
}

export interface ImportSelfPurchaseCostResult {
  totalRows: number;
  validRows: number;
  matchedSelfPurchaseRows: number;
  invalidRows: number;
  duplicateInFile: number;
  insertedRows: number;
  updatedRows: number;
  skippedExisting: number;
  skippedNoOrderData: number;
  skippedNonSelfPurchase: number;
  warehouseSourceConflict: number;
  warehouseSourceConflictIds: string[];
}

class OperatingDataAPI {
  /**
   * 获取经营数据总览
   */
  async getOverview(params: OperatingDataRequest): Promise<BaseResponse<OperatingDataOverviewResponse>> {
    const response = await apiClient.post<BaseResponse<OperatingDataOverviewResponse>>(
      '/operating-data/overview',
      params
    );
    return response.data;
  }

  /**
   * 添加手动输入数据
   */
  async addManualData(params: ManualDataRequest): Promise<BaseResponse<ManualData>> {
    const response = await apiClient.post<BaseResponse<ManualData>>(
      '/operating-data/manual',
      params
    );
    return response.data;
  }

  /**
   * 获取手动输入数据列表
   */
  async listManualData(params: any): Promise<BaseResponse<{ items: ManualData[]; pagination: any }>> {
    const response = await apiClient.post<BaseResponse<{ items: ManualData[]; pagination: any }>>(
      '/operating-data/manual/list',
      params
    );
    return response.data;
  }

  /**
   * 删除手动输入数据
   */
  async deleteManualData(id: number): Promise<BaseResponse<string>> {
    const response = await apiClient.delete<BaseResponse<string>>(
      `/operating-data/manual/${id}`
    );
    return response.data;
  }

  /**
   * 导入自采设备成本Excel
   */
  async importSelfPurchaseCost(file: File): Promise<BaseResponse<ImportSelfPurchaseCostResult>> {
    const formData = new FormData();
    formData.append('file', file);
    const response = await apiClient.post<BaseResponse<ImportSelfPurchaseCostResult>>(
      '/operating-data/import-self-purchase-cost',
      formData,
      {
        headers: {
          'Content-Type': 'multipart/form-data',
        },
      }
    );
    return response.data;
  }
}

export const operatingDataApi = new OperatingDataAPI();
export default operatingDataApi;
